import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const WelcomeContext = createContext(null);

const STORAGE_KEY = 'damyxia-welcome-completed';
const WELCOME_VERSION = '1.0'; // Increment to force re-show after major updates

export const WelcomeProvider = ({ children }) => {
  const [isFirstVisit, setIsFirstVisit] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [welcomeCompleted, setWelcomeCompleted] = useState(false);
  const [emotionalTone, setEmotionalTone] = useState('neutral'); // neutral | calm | energetic

  // Check first visit status on mount
  useEffect(() => {
    const checkFirstVisit = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        
        if (!stored) {
          // First time visitor
          setIsFirstVisit(true);
          setShowWelcome(true);
          setWelcomeCompleted(false);
        } else {
          // Parse stored data
          const data = JSON.parse(stored);
          
          // Check if welcome version matches (to re-show after major updates)
          if (data.version !== WELCOME_VERSION) {
            setIsFirstVisit(false); // Not first visit, but new version
            setShowWelcome(true);
            setWelcomeCompleted(false);
          } else {
            setIsFirstVisit(false);
            setShowWelcome(false);
            setWelcomeCompleted(true);
          }
        }
      } catch (error) {
        // If parsing fails, treat as first visit
        console.error('Error reading welcome state:', error);
        setIsFirstVisit(true);
        setShowWelcome(true);
      }
    };

    // Small delay to allow other contexts to initialize
    const timer = setTimeout(checkFirstVisit, 100);
    return () => clearTimeout(timer);
  }, []);

  // Complete the welcome flow
  const completeWelcome = useCallback(() => {
    try {
      const data = {
        version: WELCOME_VERSION,
        completedAt: new Date().toISOString(),
        platform: detectPlatform(),
        language: localStorage.getItem('damyxia-language') || 'es'
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      setShowWelcome(false);
      setWelcomeCompleted(true);
    } catch (error) {
      console.error('Error saving welcome state:', error);
      setShowWelcome(false);
    }
  }, []);

  // Reset welcome (for settings option)
  const resetWelcome = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
      setIsFirstVisit(true);
      setShowWelcome(true);
      setWelcomeCompleted(false);
    } catch (error) {
      console.error('Error resetting welcome:', error);
    }
  }, []);

  // Skip welcome without marking as complete
  const skipWelcome = useCallback(() => {
    setShowWelcome(false);
  }, []);

  // Set emotional tone from AEAE or other sources
  const setTone = useCallback((tone) => {
    if (['neutral', 'calm', 'energetic'].includes(tone)) {
      setEmotionalTone(tone);
    }
  }, []);

  const value = {
    isFirstVisit,
    showWelcome,
    welcomeCompleted,
    emotionalTone,
    completeWelcome,
    resetWelcome,
    skipWelcome,
    setTone,
  };

  return (
    <WelcomeContext.Provider value={value}>
      {children}
    </WelcomeContext.Provider>
  );
};

// Helper function to detect platform
const detectPlatform = () => {
  const ua = navigator.userAgent || navigator.vendor || window.opera;
  
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) {
    return 'ios';
  }
  if (/android/i.test(ua)) {
    return 'android';
  }
  if (window.innerWidth >= 1024) {
    return 'desktop';
  }
  return 'mobile';
};

// Custom hook to use Welcome context
export const useWelcome = () => {
  const context = useContext(WelcomeContext);
  if (!context) {
    throw new Error('useWelcome must be used within a WelcomeProvider');
  }
  return context;
};

export default WelcomeContext;
